import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { createAppTheme } from './configuration/theme';
import { LanguageProvider } from './shared/contexts/LanguageContext';
import { ThemeProvider, useThemeMode } from './shared/contexts/ThemeContext';
import { CookieConsentProvider } from './adapters/primary/CookieConsentContext';
import { Layout } from './adapters/primary/Layout';
import { HomePage } from './adapters/primary/HomePage';
import { AboutUsPage } from './adapters/primary/AboutUsPage';
import { WhatIsMEPage } from './adapters/primary/WhatIsMEPage';
import { MitmachenPage } from './adapters/primary/MitmachenPage';
import { DonationPage } from './adapters/primary/DonationPage';
import { ImpressumPage } from './adapters/primary/ImpressumPage';
import { PrivacyPage } from './adapters/primary/PrivacyPage';
import { CookiePage } from './adapters/primary/CookiePage';
import { CookieSettingsPage } from './adapters/primary/CookieSettingsPage';
import { AccountDeletionPage } from './adapters/primary/AccountDeletionPage';
import { TermsPage } from './adapters/primary/TermsPage';
import { AuthCallbackPage } from './adapters/primary/AuthCallbackPage';
import { CookieBanner } from './adapters/primary/CookieBanner';
import { LanguageRedirect } from './adapters/primary/LanguageRedirect';
import { ScrollToTop } from './adapters/primary/ScrollToTop';
import { GoogleAnalytics } from './adapters/primary/GoogleAnalytics';
import { DEFAULT_LANGUAGE, LANGUAGES, ROUTES } from './shared/constants/routes';

// Resolves the component names in ROUTES, which stays free of imports so that
// scripts/prerender.js can load it at build time.
const PAGE_COMPONENTS: Record<string, React.ReactElement> = {
  HomePage: <HomePage />,
  AboutUsPage: <AboutUsPage />,
  WhatIsMEPage: <WhatIsMEPage />,
  MitmachenPage: <MitmachenPage />,
  DonationPage: <DonationPage />,
  ImpressumPage: <ImpressumPage />,
  PrivacyPage: <PrivacyPage />,
  CookiePage: <CookiePage />,
  CookieSettingsPage: <CookieSettingsPage />,
  AccountDeletionPage: <AccountDeletionPage />,
  TermsPage: <TermsPage />,
};

const pageElement = (component: string): React.ReactElement => {
  const element = PAGE_COMPONENTS[component];
  if (!element) {
    throw new Error(`No component registered for "${component}" - see ROUTES in routes.ts`);
  }
  return element;
};

// Inner component that has access to theme mode
const AppContent: React.FC = () => {
  const { mode } = useThemeMode();
  const theme = React.useMemo(() => createAppTheme(mode), [mode]);

  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      <CookieConsentProvider>
        <Router>
          <LanguageProvider>
            <GoogleAnalytics />
            <ScrollToTop />
            <Layout>
              <Routes>
                {/* The default language sits on the bare paths, every other
                    language under its own prefix. Both get their own meta tags
                    and hreflang from scripts/prerender.js. */}
                {ROUTES.map((route) => (
                  <Route
                    key={route.path}
                    path={route.path}
                    element={pageElement(route.component)}
                  />
                ))}
                {LANGUAGES.filter((lang) => lang !== DEFAULT_LANGUAGE).map((lang) => (
                  <Route key={lang} path={`/${lang}`}>
                    {ROUTES.map((route) =>
                      route.path === '/' ? (
                        <Route key={route.path} index element={pageElement(route.component)} />
                      ) : (
                        <Route
                          key={route.path}
                          path={route.path.slice(1)}
                          element={pageElement(route.component)}
                        />
                      )
                    )}
                  </Route>
                ))}
                {/* Auth bridge — Supabase redirects the confirmation/reset link
                    here, this page forwards into the mobile app. Not language-
                    prefixed and not prerendered; served via the SPA fallback.
                    Must sit before the catch-all so LanguageRedirect ignores it. */}
                <Route path="/auth/callback" element={<AuthCallbackPage />} />
                <Route path="*" element={<LanguageRedirect />} />
              </Routes>
            </Layout>
            <CookieBanner />
          </LanguageProvider>
        </Router>
      </CookieConsentProvider>
    </MuiThemeProvider>
  );
};

function App() {
  return (
    <ThemeProvider>
      <AppContent />
    </ThemeProvider>
  );
}

export default App;
